import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Mail, Briefcase, Video, Mic, FileText, AlertTriangle, CheckCircle2, KeyRound } from "lucide-react";

type ProviderKey = "google" | "asana" | "zoom" | "otter" | "notion";
type AuthMode = "oauth" | "shared_secret";

type ProviderMeta = {
  key: ProviderKey;
  name: string;
  icon: any;
  unlocks: string;
  watchRules: string[];
  scopes: string[];
  // Full Google scope URLs a stored grant MUST carry. A grant made before a scope was added keeps
  // working for everything else, so nothing surfaces the gap — hence the "Reconnect required" badge.
  requiredScopes?: { scope: string; why: string }[];
  built: boolean;
  authMode: AuthMode;
};

const PROVIDERS: ProviderMeta[] = [
  {
    key: "google", name: "Google", icon: Mail,
    unlocks: "Gmail (fund-manager emails), Drive (decks, PDFs, sheets), Calendar (agendas)",
    watchRules: ["gmail_query", "gmail_label", "drive_folder", "calendar_keyword", "gsheet_range"],
    scopes: ["gmail.readonly", "drive (read + write)", "calendar.readonly", "calendar.events", "spreadsheets.readonly", "userinfo.email"],
    requiredScopes: [
      { scope: "https://www.googleapis.com/auth/drive", why: "generate + file fund appraisal reports in Drive" },
      { scope: "https://www.googleapis.com/auth/calendar.events", why: "book fund meetings on the shared calendar" },
    ],
    built: true, authMode: "oauth",
  },
  {
    key: "asana", name: "Asana", icon: Briefcase,
    unlocks: "Asana task ingest, comments, attachments",
    watchRules: ["asana_project"],
    scopes: ["ASANA_ACCESS_TOKEN"],
    built: true, authMode: "shared_secret",
  },
  {
    key: "zoom", name: "Zoom", icon: Video,
    unlocks: "Zoom recordings, chat history, AI Companion notes, whiteboards",
    watchRules: ["zoom_meeting_keyword", "zoom_chat_channel", "zoom_ai_notes", "zoom_whiteboard"],
    scopes: ["ZOOM_ACCOUNT_ID", "ZOOM_CLIENT_ID", "ZOOM_CLIENT_SECRET"],
    built: true, authMode: "shared_secret",
  },
  {
    key: "otter", name: "Otter", icon: Mic,
    unlocks: "Otter transcripts of fund-manager calls",
    watchRules: ["otter_keyword"],
    scopes: ["OTTER_API_KEY"],
    built: true, authMode: "shared_secret",
  },
  {
    key: "notion", name: "Notion", icon: FileText,
    unlocks: "Notion page ingest (parity; no rules use this today)",
    watchRules: [],
    scopes: ["NOTION_TOKEN"],
    built: false, authMode: "shared_secret",
  },
];

type StatusRow = {
  provider: string;
  connected: boolean;
  expires_at: string | null;
  expires_soon: boolean;
  last_refreshed_at: string | null;
};

type SecretStatus = Record<string, { configured: boolean; missing: string[]; required: string[] }>;

export default function ResearchConnectors() {
  const { data: canManage, isLoading: capLoading } = useResearchCapability("manage");
  const qc = useQueryClient();
  const [connecting, setConnecting] = useState<ProviderKey | null>(null);

  const { data: statuses, isLoading } = useQuery({
    queryKey: ["research_oauth_status"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_research_oauth_status");
      if (error) throw error;
      return (data ?? []) as StatusRow[];
    },
    enabled: canManage === true,
  });

  const { data: credRows } = useQuery({
    queryKey: ["research_oauth_credentials_meta"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_oauth_credentials")
        .select("provider, scope, external_user_id");
      if (error) throw error;
      return data ?? [];
    },
    enabled: canManage === true,
  });

  const { data: secretStatus } = useQuery({
    queryKey: ["research_connector_secret_status"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("research-connector-status", { body: {} });
      if (error) throw error;
      return (data as any)?.status as SecretStatus;
    },
    enabled: canManage === true,
  });

  if (capLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!canManage) return <p className="text-sm text-muted-foreground">Requires manage capability.</p>;

  const statusByProvider = new Map((statuses ?? []).map((s) => [s.provider, s]));
  const credByProvider = new Map((credRows ?? []).map((c: any) => [c.provider, c]));

  const handleConnect = async (p: ProviderMeta) => {
    if (!p.built) {
      toast.info(`${p.name} integration not yet built — coming soon.`);
      return;
    }
    if (p.authMode === "shared_secret") {
      toast.info(`${p.name} uses shared workspace credentials stored as Supabase secrets. Manage them in Project Settings → Edge Function Secrets.`);
      return;
    }
    setConnecting(p.key);
    const redirectUri = `${window.location.origin}/partner/research/connectors/callback`;
    const { data, error } = await supabase.functions.invoke(`research-oauth-init-${p.key}`, {
      body: { redirectUri, state: p.key },
    });
    setConnecting(null);
    const resp = data as any;
    if (error || !resp?.authUrl) {
      toast.error(`Failed to start OAuth for ${p.name}`);
      return;
    }
    window.location.href = resp.authUrl;
  };

  const handleDisconnect = async (p: ProviderMeta) => {
    if (p.authMode === "shared_secret") return;
    const { error } = await supabase
      .from("research_oauth_credentials")
      .delete()
      .eq("provider", p.key);
    if (error) { toast.error(error.message); return; }
    toast.success(`${p.name} disconnected`);
    qc.invalidateQueries({ queryKey: ["research_oauth_status"] });
    qc.invalidateQueries({ queryKey: ["research_oauth_credentials_meta"] });
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Connectors</h2>
        <p className="text-sm text-muted-foreground">
          Credentials powering the research ingest pipelines. Shared connectors use a single workspace account
          stored as Supabase secrets; Google uses per-user OAuth.
        </p>
      </div>

      <div className="space-y-3">
        {PROVIDERS.map((p) => {
          const s = statusByProvider.get(p.key);
          const cred: any = credByProvider.get(p.key);
          const Icon = p.icon;
          const secret = secretStatus?.[p.key];
          const connected =
            p.authMode === "shared_secret"
              ? Boolean(secret?.configured)
              : Boolean(s?.connected);

          // Scopes this connector needs that the STORED grant does not carry (i.e. it was authorised
          // before the scope was added). Everything else keeps working, so this is the only signal.
          const grantedScopes = new Set(String(cred?.scope ?? "").split(/\s+/).filter(Boolean));
          const missingScopes =
            p.authMode === "oauth" && connected
              ? (p.requiredScopes ?? []).filter((r) => !grantedScopes.has(r.scope))
              : [];

          return (
            <Card key={p.key}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="rounded-md bg-muted p-2"><Icon className="h-5 w-5 text-primary" /></div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">{p.name}</h3>
                        {connected ? (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1">
                            <CheckCircle2 className="h-3 w-3" /> {p.authMode === "shared_secret" ? "Configured" : "Connected"}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            {p.authMode === "shared_secret" ? "Secret missing" : "Not connected"}
                          </Badge>
                        )}
                        {p.authMode === "shared_secret" && (
                          <Badge variant="outline" className="gap-1 text-[10px]">
                            <KeyRound className="h-3 w-3" /> Shared workspace credential
                          </Badge>
                        )}
                        {p.authMode === "oauth" && connected && s?.expires_soon && (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1">
                            <AlertTriangle className="h-3 w-3" /> Expires soon
                          </Badge>
                        )}
                        {missingScopes.length > 0 && (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1">
                            <AlertTriangle className="h-3 w-3" /> Reconnect required
                          </Badge>
                        )}
                        {!p.built && (
                          <Badge variant="outline" className="text-xs">Coming soon</Badge>
                        )}
                      </div>

                      {p.authMode === "shared_secret" ? (
                        <div className="text-xs text-muted-foreground space-y-2">
                          <p>{p.unlocks}</p>
                          <div>
                            <span className="font-medium text-foreground">Supabase secrets:</span>{" "}
                            {p.scopes.map((sec) => {
                              const missing = secret?.missing.includes(sec);
                              return (
                                <code
                                  key={sec}
                                  className={`mx-0.5 px-1 py-0.5 rounded text-[10px] ${
                                    missing ? "bg-destructive/10 text-destructive" : "bg-emerald-100 text-emerald-800"
                                  }`}
                                >
                                  {sec}
                                </code>
                              );
                            })}
                          </div>
                          {p.watchRules.length > 0 && (
                            <div>
                              <span className="font-medium text-foreground">Watch rules:</span>{" "}
                              {p.watchRules.map((r) => (
                                <code key={r} className="mx-0.5 px-1 py-0.5 rounded bg-muted text-[10px]">{r}</code>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : connected ? (
                        <div className="text-xs text-muted-foreground space-y-1">
                          {missingScopes.length > 0 && (
                            <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-900">
                              <p className="font-medium">
                                This grant predates {missingScopes.length === 1 ? "a scope" : "some scopes"} the
                                platform now needs. Click <span className="font-semibold">Reconnect</span> to grant:
                              </p>
                              <ul className="mt-1 list-disc pl-4">
                                {missingScopes.map((r) => (
                                  <li key={r.scope}>
                                    <code className="text-[10px]">{r.scope.replace("https://www.googleapis.com/auth/", "")}</code>
                                    {" — "}{r.why}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {s?.last_refreshed_at && (
                            <div>Last refreshed: {formatDistanceToNow(new Date(s.last_refreshed_at), { addSuffix: true })}</div>
                          )}
                          {s?.expires_at && (
                            <div>Token expires: {formatDistanceToNow(new Date(s.expires_at), { addSuffix: true })}</div>
                          )}
                          {cred?.external_user_id && <div>Account: {cred.external_user_id}</div>}
                          {cred?.scope && (
                            <div className="flex flex-wrap gap-1 pt-1">
                              {String(cred.scope).split(/\s+/).filter(Boolean).map((sc: string) => (
                                <Badge key={sc} variant="secondary" className="text-[10px] font-normal">{sc}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground space-y-2">
                          <p>{p.unlocks}</p>
                          <div>
                            <span className="font-medium text-foreground">Scopes:</span>{" "}
                            {p.scopes.join(", ")}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 shrink-0">
                    {p.authMode === "oauth" ? (
                      connected ? (
                        <>
                          <ConnectButton p={p} loading={connecting === p.key} onClick={() => handleConnect(p)} label="Reconnect" variant="outline" />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm">Disconnect</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Disconnect {p.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Disabling this connector will cause all watch rules using it to fail until reconnected. Past ingested sources remain.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDisconnect(p)}>Disconnect</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      ) : (
                        <ConnectButton p={p} loading={connecting === p.key} onClick={() => handleConnect(p)} label="Connect" />
                      )
                    ) : (
                      <Tooltip>
                        <TooltipProvider>
                          <TooltipTrigger asChild>
                            <span>
                              <Button size="sm" variant="outline" disabled>
                                {connected ? "Managed via secrets" : "Add secret"}
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {connected
                              ? "Update the secret in Project Settings → Edge Function Secrets."
                              : `Add ${p.scopes.join(", ")} in Project Settings → Edge Function Secrets.`}
                          </TooltipContent>
                        </TooltipProvider>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {isLoading && <p className="text-xs text-muted-foreground">Loading connection status…</p>}
    </div>
  );
}

function ConnectButton({
  p, loading, onClick, label, variant = "default",
}: { p: ProviderMeta; loading: boolean; onClick: () => void; label: string; variant?: "default" | "outline" }) {
  return (
    <Button size="sm" variant={variant} disabled={!p.built || loading} onClick={onClick}>
      {loading ? "Starting…" : label}
    </Button>
  );
}
